import { initLazyImages } from '~/scripts/utils/lazy-images.js';
import { afterAnimationEnd } from '~/scripts/utils/utils.js';
import { populateTemplate } from '~/scripts/utils/populate-template.js';
import { trackAddToCartFromComparison } from '~/scripts/analytics/events.js';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

// Separate concerns into smaller classes
class BikeStorage {
    constructor(storageKey, maxBikes) {
        this.storageKey = storageKey;
        this.maxBikes = maxBikes;
    }

    load() {
        try {
            const stored = localStorage.getItem(this.storageKey);
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            console.error('Failed to load bikes from storage:', e);
            return [];
        }
    }

    save(bikes) {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(bikes));
        } catch (e) {
            console.error('Failed to save bikes to storage:', e);
        }
    }

    clear() {
        try {
            localStorage.removeItem(this.storageKey);
        } catch (e) {
            console.error('Failed to clear bikes from storage:', e);
        }
    }
}

class ProductDataCache {
    constructor() {
        this.cache = new Map();
        this.pendingRequests = new Map();
    }

    get(handle) {
        return this.cache.get(handle);
    }

    set(handle, data) {
        this.cache.set(handle, data);
    }

    has(handle) {
        return this.cache.has(handle);
    }

    clear() {
        this.cache.clear();
        this.pendingRequests.clear();
    }

    async fetch(handle, fetcher) {
        // Return cached data if available
        if (this.has(handle)) {
            return this.get(handle);
        }

        // Return existing pending request if one exists
        if (this.pendingRequests.has(handle)) {
            return this.pendingRequests.get(handle);
        }

        // Create new request
        const request = fetcher(handle)
            .then((data) => {
                if (data) {
                    this.set(handle, data);
                }
                this.pendingRequests.delete(handle);
                return data;
            })
            .catch((error) => {
                this.pendingRequests.delete(handle);
                throw error;
            });

        this.pendingRequests.set(handle, request);
        return request;
    }
}

class UIManager {
    constructor(container) {
        this.container = container;
        this.elements = new Map();
    }

    getElement(selector, parent = this.container) {
        const key = `${parent === this.container ? 'root' : 'child'}-${selector}`;

        if (!this.elements.has(key)) {
            const element = parent.querySelector(selector);
            if (element) {
                this.elements.set(key, element);
            }
        }

        return this.elements.get(key);
    }

    clearCache() {
        this.elements.clear();
    }

    showNotification(message, type = 'info') {
        const notification = document.createElement('div');
        notification.className = `
            fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-2xl 
            transform translate-x-full transition-all duration-500 
            backdrop-blur-sm border
            ${
                type === 'error'
                    ? 'bg-red-600 text-white border-red-700'
                    : type === 'success'
                      ? 'bg-green-600 text-white border-green-700'
                      : 'bg-gradient-to-r from-gray-900 to-black text-white border-gray-700'
            }
        `;
        notification.textContent = message;
        notification.setAttribute('role', 'alert');
        notification.setAttribute('aria-live', 'polite');

        document.body.appendChild(notification);

        // Use requestAnimationFrame for smoother animations
        requestAnimationFrame(() => {
            notification.classList.remove('translate-x-full');
            notification.classList.add('translate-x-0');
        });

        setTimeout(() => {
            notification.classList.add('translate-x-full');
            notification.classList.remove('translate-x-0');
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }
}

class BikeComparison extends HTMLElement {
    // Constants
    static MAX_BIKES = 4;
    static STORAGE_KEY = 'dailyride-compare-bikes';
    static MIN_BIKES_FOR_COMPARISON = 2;
    static FETCH_TIMEOUT = 5000;

    // DOM Selectors
    static SELECTORS = {
        modal: '#bike-comparison-modal',
        loadingOverlay: '[data-loading-overlay]',
        comparisonTable: '[data-comparison-table]',
        tableHead: '[data-table-head]',
        tableBody: '[data-table-body]',
        swiperWrapper: '[data-swiper-wrapper]',
        mobileSlider: '[data-mobile-slider]',
        compareButton: '[data-compare-now]',
        clearButton: '[data-clear-all]',
        bikesContainer: '[data-bikes-container]',
        bikeCount: '[data-bike-count]'
    };

    constructor() {
        super();

        // Initialize services
        this.storage = new BikeStorage(BikeComparison.STORAGE_KEY, BikeComparison.MAX_BIKES);
        this.productCache = new ProductDataCache();
        this.uiManager = new UIManager(document.body);

        // State
        this.bikes = this.storage.load();
        this.isTableLoaded = false;
        this.mobileSwiper = null;

        // Cached DOM elements
        this.modal = null;
        this.comparisonBar = null;

        // Bound methods for event listeners
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);

        this.init();
    }

    async init() {
        try {
            this.createComparisonBar();
            this.createComparisonModal();
            this.attachEventListeners();
            this.updateUI();

            // Preload product data after a short delay
            setTimeout(() => this.preloadProductData(), 100);
        } catch (error) {
            console.error('Failed to initialize bike comparison:', error);
            this.uiManager.showNotification('Failed to initialize comparison tool', 'error');
        }
    }

    // Validation utilities
    isValidBike(bike) {
        return (
            bike &&
            typeof bike.id === 'string' &&
            typeof bike.handle === 'string' &&
            typeof bike.title === 'string' &&
            bike.id.length > 0 &&
            bike.handle.length > 0 &&
            bike.title.length > 0
        );
    }

    // Bike management
    addBike(bikeData) {
        if (!this.isValidBike(bikeData)) {
            console.warn('Invalid bike data provided:', bikeData);
            return false;
        }

        if (this.bikes.length >= BikeComparison.MAX_BIKES) {
            const message =
                window.themeVariables?.strings?.comparison?.max_bikes_message?.replace(
                    '{{ count }}',
                    BikeComparison.MAX_BIKES
                ) || `Maximum ${BikeComparison.MAX_BIKES} bikes allowed`;
            this.uiManager.showNotification(message);
            return false;
        }

        const exists = this.bikes.some((bike) => bike.id === bikeData.id);
        if (exists) {
            const message =
                window.themeVariables?.strings?.comparison?.already_added_message ||
                'This bike is already in comparison';
            this.uiManager.showNotification(message);
            return false;
        }

        this.bikes.push(bikeData);
        this.storage.save(this.bikes);
        this.updateUI();

        Shopify.analytics.publish('product_added_to_comparison', {
            product_id: bikeData.id,
            product_handle: bikeData.handle,
            product_title: bikeData.title
        });

        const message =
            window.themeVariables?.strings?.comparison?.bike_added_message || 'Bike added to comparison';
        this.uiManager.showNotification(message, 'success');

        // Preload product data for the new bike
        this.loadProductData(bikeData.handle);

        return true;
    }

    removeBike(bikeId) {
        const initialLength = this.bikes.length;
        this.bikes = this.bikes.filter((bike) => bike.id !== bikeId);

        if (this.bikes.length < initialLength) {
            this.storage.save(this.bikes);
            this.updateUI();
            return true;
        }
        return false;
    }

    clearAll() {
        this.bikes = [];
        this.storage.clear();
        this.updateUI();
    }

    // UI Creation
    createComparisonBar() {
        const template = document.getElementById('comparison-bar-template');
        if (!template) {
            console.error('comparison-bar-template not found');
            return;
        }

        const fragment = template.content.cloneNode(true);
        this.comparisonBar = fragment.firstElementChild;

        if (!this.comparisonBar) {
            console.error('Failed to create comparison bar from template');
            return;
        }

        document.body.appendChild(this.comparisonBar);
    }

    createComparisonModal() {
        this.modal = document.getElementById('bike-comparison-modal');

        if (!this.modal) {
            console.error(
                'Bike comparison modal not found. Ensure bike-comparison-modal section is included.'
            );
            return;
        }

        // Theme editor preview support
        if (window.Shopify?.designMode && window.location.hash === '#bike-comparison-modal') {
            setTimeout(() => this.showThemeEditorDemo(), 500);
        }
    }

    // UI Updates
    updateUI() {
        requestAnimationFrame(() => {
            this.updateComparisonBar();
            this.updateCompareButtons();
            this.toggleComparisonBarVisibility();
        });
    }

    updateComparisonBar() {
        const bikesContainer = this.uiManager.getElement(
            BikeComparison.SELECTORS.bikesContainer,
            this.comparisonBar
        );
        const bikeCount = this.uiManager.getElement(BikeComparison.SELECTORS.bikeCount, this.comparisonBar);
        const compareButton = this.uiManager.getElement(
            BikeComparison.SELECTORS.compareButton,
            this.comparisonBar
        );

        if (!bikesContainer) return;

        // Get the template
        const template = document.getElementById('comparison-bar-bike-item-template');
        if (!template) {
            console.error('comparison-bar-bike-item-template not found');
            return;
        }

        // Use DocumentFragment for better performance
        const fragment = document.createDocumentFragment();

        this.bikes.forEach((bike) => {
            const variables = {
                bike_id: bike.id,
                bike_image: bike.image,
                bike_title: bike.title
            };

            const bikeItemFragment = populateTemplate(template, variables);
            if (bikeItemFragment) {
                fragment.appendChild(bikeItemFragment);
            }
        });

        bikesContainer.innerHTML = '';
        bikesContainer.appendChild(fragment);

        if (bikeCount) {
            bikeCount.textContent = this.bikes.length;
        }

        // Update compare button state
        if (compareButton) {
            compareButton.disabled = this.bikes.length < BikeComparison.MIN_BIKES_FOR_COMPARISON;
        }
    }

    updateCompareButtons() {
        const isComparisonFull = this.bikes.length >= BikeComparison.MAX_BIKES;
        const buttons = document.querySelectorAll('[data-compare-button]');

        buttons.forEach((button) => {
            const bikeId = button.dataset.bikeId;
            const isInComparison = this.bikes.some((bike) => bike.id === bikeId);

            this.updateCompareButton(button, isInComparison, isComparisonFull);
        });
    }

    updateCompareButton(button, isInComparison, isComparisonFull) {
        // Remove all state classes
        button.className = button.className.replace(/bg-\S+|text-\S+|hover:bg-\S+|cursor-\S+/g, '').trim();

        let templateId;
        if (isInComparison) {
            button.classList.add('bg-green-500', 'text-white', 'hover:bg-green-600');
            button.disabled = false;
            templateId = 'compare-button-added-template';
        } else if (isComparisonFull) {
            button.classList.add('bg-gray-400', 'text-white', 'cursor-not-allowed');
            button.disabled = true;
            templateId = 'compare-button-full-template';
        } else {
            button.classList.add('bg-white', 'text-black', 'hover:bg-gray-50');
            button.disabled = false;
            templateId = 'compare-button-compare-template';
        }

        const template = document.getElementById(templateId);
        if (template) {
            const fragment = template.content.cloneNode(true);
            button.innerHTML = '';
            button.appendChild(fragment);
        }
    }

    toggleComparisonBarVisibility() {
        // Hide comparison bar on product pages since they have their own comparison section
        const isProductPage = window.location.pathname.includes('/products/') && 
                              !window.location.pathname.includes('/products.json');
        
        if (isProductPage) {
            this.comparisonBar.classList.add('translate-y-full');
            this.comparisonBar.classList.remove('translate-y-0');
            return;
        }
        
        // Normal behavior for other pages
        if (this.bikes.length > 0) {
            this.comparisonBar.classList.remove('translate-y-full');
            this.comparisonBar.classList.add('translate-y-0');
        } else {
            this.comparisonBar.classList.add('translate-y-full');
            this.comparisonBar.classList.remove('translate-y-0');
        }
    }

    // Modal management
    async showComparisonModal() {
        if (this.bikes.length < BikeComparison.MIN_BIKES_FOR_COMPARISON) {
            const message =
                window.themeVariables?.strings?.comparison?.min_bikes_message ||
                'Please select at least 2 bikes to compare';
            this.uiManager.showNotification(message);
            return;
        }

        if (!this.modal) {
            console.error('Bike comparison modal not found');
            return;
        }

        try {
            // Show modal
            this.modal.classList.remove('hidden');
            this.modal.classList.add('flex');
            document.body.classList.add('overflow-hidden');

            // Focus management
            const popup = this.modal.querySelector('.popup');
            if (popup) {
                popup.focus();
            }

            // Animation
            this.modal.classList.add('is-opening');
            afterAnimationEnd(popup || this.modal, () => {
                this.modal.classList.remove('is-opening');
            });

            // Load data and update views
            await this.loadModalData();
        } catch (error) {
            console.error('Failed to show comparison modal:', error);
            this.uiManager.showNotification('Failed to load comparison', 'error');
            this.hideComparisonModal();
        }
    }

    async loadModalData() {
        const missingHandles = this.getMissingHandles();

        if (missingHandles.length > 0) {
            this.showLoading();
            await this.fetchMissingProducts(missingHandles);
        }

        await Promise.all([this.updateComparisonTable(), this.updateMobileSlider()]);

        this.hideLoading();
    }

    hideComparisonModal() {
        if (!this.modal) return;

        // Cleanup
        if (this.mobileSwiper) {
            this.mobileSwiper.destroy(true, true);
            this.mobileSwiper = null;
        }

        // Animation
        this.modal.classList.add('is-closing');

        const popup = this.modal.querySelector('.popup');
        afterAnimationEnd(popup || this.modal, () => {
            this.modal.classList.add('hidden');
            this.modal.classList.remove('flex', 'is-closing');
            document.body.classList.remove('overflow-hidden');
        });
    }

    // Loading states
    showLoading() {
        const overlays = this.modal?.querySelectorAll(BikeComparison.SELECTORS.loadingOverlay) || [];
        overlays.forEach((overlay) => overlay.classList.remove('hidden'));
    }

    hideLoading() {
        const overlays = this.modal?.querySelectorAll(BikeComparison.SELECTORS.loadingOverlay) || [];
        overlays.forEach((overlay) => overlay.classList.add('hidden'));
    }

    // Data fetching
    getMissingHandles() {
        return this.bikes
            .filter((bike) => this.isValidBike(bike))
            .map((bike) => bike.handle)
            .filter((handle) => !this.productCache.has(handle));
    }

    async fetchMissingProducts(handles) {
        const fetchPromises = handles.map((handle) =>
            this.productCache.fetch(handle, this.fetchProductComparisonData.bind(this)).catch((error) => {
                console.error(`Failed to fetch product ${handle}:`, error);
                return null;
            })
        );

        const results = await Promise.all(fetchPromises);
        const successful = results.filter(Boolean).length;
        const failed = results.length - successful;

        if (failed > 0) {
            console.warn(`Failed to fetch ${failed} products`);
        }

        return { successful, failed };
    }

    async loadProductData(handle) {
        return this.productCache.fetch(handle, this.fetchProductComparisonData.bind(this));
    }

    async fetchProductComparisonData(handle) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), BikeComparison.FETCH_TIMEOUT);

        try {
            const response = await fetch(`/products/${handle}?view=comparison`, {
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseText = await response.text();
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = responseText;

            const scriptTag = tempDiv.querySelector('#product-comparison-data');
            if (!scriptTag) {
                throw new Error('Product comparison data not found in response');
            }

            const jsonText = scriptTag.textContent.trim();
            if (!jsonText) {
                throw new Error('Empty product comparison data');
            }

            return JSON.parse(jsonText);
        } catch (error) {
            if (error.name === 'AbortError') {
                console.error(`Fetch timeout for product ${handle}`);
            } else {
                console.error(`Failed to fetch product data for ${handle}:`, error);
            }
            return null;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    // Table updates
    async updateComparisonTable() {
        if (!this.modal) return;

        const table = this.modal.querySelector(BikeComparison.SELECTORS.comparisonTable);
        if (!table) return;

        try {
            await this.buildTableContent();
        } catch (error) {
            console.error('Error updating comparison table:', error);
        }
    }

    async buildTableContent() {
        const products = this.bikes.map((bike) => this.productCache.get(bike.handle)).filter(Boolean);

        if (products.length === 0) {
            console.warn('No valid product data found for table');
            return;
        }

        const thead = this.modal.querySelector(BikeComparison.SELECTORS.tableHead);
        const tbody = this.modal.querySelector(BikeComparison.SELECTORS.tableBody);
        const headerRow = thead?.querySelector('tr');
        const existingRows = tbody?.querySelectorAll('.spec-row');

        if (!headerRow || !existingRows) {
            console.error('Table elements not found');
            return;
        }

        this.buildTableHeaders(headerRow, products);
        this.buildTableRows(existingRows, products);
    }

    buildTableHeaders(headerRow, products) {
        // Clear existing product headers
        const existingHeaders = headerRow.querySelectorAll('th:not(:first-child)');
        existingHeaders.forEach((header) => header.remove());

        const fragment = document.createDocumentFragment();
        const columnWidth = `calc((100% - 200px) / ${products.length})`;

        products.forEach((_, index) => {
            const bike = this.bikes[index];
            const headerCell = this.createTableHeaderCell(bike, columnWidth);
            fragment.appendChild(headerCell);
        });

        headerRow.appendChild(fragment);
    }

    createTableHeaderCell(bike, width) {
        const headerCell = document.createElement('th');
        headerCell.className = '!p-4 text-center border-b border-gray-200';
        headerCell.style.width = width;

        headerCell.innerHTML = `
            <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm transition-all hover:shadow-lg hover:-translate-y-1 duration-300">
                <div class="mb-4">
                    <div class="bg-gray-50 rounded-lg p-3 aspect-square flex items-center justify-center">
                        <img src="${bike.image}" 
                             alt="${bike.title}" 
                             class="w-full h-full object-contain" 
                             loading="lazy" 
                             width="120" 
                             height="120">
                    </div>
                </div>
                <div class="text-center space-y-3">
                    <h3 class="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight">${bike.title}</h3>
                    <div class="space-y-2">
                        <button class="group relative w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium text-sm transform hover:scale-[1.02] active:scale-[0.98] shadow-md overflow-hidden" 
                                data-add-to-cart 
                                data-product-handle="${bike.handle}"
                                aria-label="Add ${bike.title} to cart">
                            <span class="relative z-10">Add to Cart</span>
                        </button>
                        <button class="text-xs text-gray-500 hover:text-red-600 transition-all underline hover:scale-105" 
                                data-remove-from-comparison 
                                data-bike-id="${bike.id}"
                                aria-label="Remove ${bike.title} from comparison">
                            Remove
                        </button>
                    </div>
                </div>
            </div>
        `;

        return headerCell;
    }

    buildTableRows(existingRows, products) {
        const columnWidth = `calc((100% - 200px) / ${products.length})`;

        existingRows.forEach((row) => {
            const specKey = row.dataset.specKey;

            // Clear existing cells
            const existingCells = row.querySelectorAll('td:not(:first-child)');
            existingCells.forEach((cell) => cell.remove());

            const fragment = document.createDocumentFragment();
            const cellValues = [];

            products.forEach((product) => {
                const value = this.getSpecValue(specKey, product);
                const cell = this.createTableDataCell(value, columnWidth);

                cellValues.push(value);
                fragment.appendChild(cell);
            });

            row.appendChild(fragment);

            // Hide row if all values are empty
            const hasValue = cellValues.some((value) => value && value !== '-' && value !== '');
            row.style.display = hasValue ? '' : 'none';
        });
    }

    getSpecValue(specKey, product) {
        if (specKey === 'price') {
            return new Intl.NumberFormat('nl-NL', {
                style: 'currency',
                currency: 'EUR'
            }).format(product.price / 100);
        } else if (specKey.startsWith('custom.') && product.metafields) {
            const cleanKey = specKey.replace('custom.', '');
            return product.metafields[cleanKey] || product.metafields[specKey] || '-';
        }
        return '-';
    }

    createTableDataCell(value, width) {
        const cell = document.createElement('td');
        cell.className = 'p-4 text-center border-b border-gray-100 text-gray-700';
        cell.style.width = width;
        cell.textContent = value;
        return cell;
    }

    // Mobile slider
    async updateMobileSlider() {
        if (!this.modal || this.bikes.length === 0) return;

        const swiperWrapper = this.modal.querySelector(BikeComparison.SELECTORS.swiperWrapper);
        if (!swiperWrapper) return;

        try {
            // Clear existing slides
            swiperWrapper.innerHTML = '';

            // Create slides
            const slidePromises = this.bikes.map((bike) => this.createMobileSlideWithSpecs(bike));
            const slides = await Promise.all(slidePromises);

            // Add slides to DOM
            const fragment = document.createDocumentFragment();
            slides.filter(Boolean).forEach((slide) => fragment.appendChild(slide));
            swiperWrapper.appendChild(fragment);

            // Initialize Swiper
            this.initializeMobileSwiper();
        } catch (error) {
            console.error('Error updating mobile slider:', error);
        }
    }

    async createMobileSlideWithSpecs(bike) {
        const slide = this.createMobileSlide(bike);
        if (!slide) return null;

        await this.populateSlideSpecs(slide, bike);
        return slide;
    }

    createMobileSlide(bike) {
        const template = document.getElementById('mobile-slide-template');
        if (!template) {
            console.error('mobile-slide-template not found');
            return null;
        }

        const variables = {
            bike_id: bike.id,
            bike_image: bike.image || bike.featured_image,
            bike_title: bike.title,
            bike_vendor: bike.vendor || '',
            bike_handle: bike.handle
        };

        try {
            const fragment = populateTemplate(template, variables);
            if (!fragment) return null;

            const slide = fragment.firstElementChild;
            if (!slide) return null;

            slide.dataset.bikeSlide = '';
            slide.dataset.bikeId = bike.id;

            return slide;
        } catch (error) {
            console.error('Error creating mobile slide:', error);
            return null;
        }
    }

    async populateSlideSpecs(slide, bike) {
        const specsContainer = slide.querySelector('[data-specs-container] .space-y-3');
        if (!specsContainer) return;

        // Get product data
        const productData = await this.loadProductData(bike.handle);
        if (!productData) return;

        // Get blocks configuration
        const blocksConfig = this.getBlocksConfiguration();
        if (!blocksConfig) return;

        // Create specs
        const fragment = document.createDocumentFragment();
        blocksConfig.blocks.forEach((block) => {
            const specElement = this.createSpecElement(block, productData);
            if (specElement) {
                fragment.appendChild(specElement);
            }
        });

        specsContainer.innerHTML = '';
        specsContainer.appendChild(fragment);
    }

    getBlocksConfiguration() {
        try {
            const blocksScript = document.getElementById('bike-comparison-blocks');
            if (!blocksScript) return null;
            return JSON.parse(blocksScript.textContent);
        } catch (error) {
            console.error('Failed to parse blocks configuration:', error);
            return null;
        }
    }

    createSpecElement(block, productData) {
        const { metafield_key, label, icon } = block;
        const value = this.getSpecValue(metafield_key, productData);

        if (!label || !value || value === '-') return null;

        const template = document.getElementById('mobile-spec-row-template');
        if (!template) return null;

        // Get icon HTML
        const iconContainer = document.querySelector(`#mobile-icon-container [data-icon="${icon}"]`);
        const iconHtml = iconContainer ? iconContainer.innerHTML : '';

        const variables = {
            spec_icon: iconHtml,
            spec_label: label,
            spec_value: value
        };

        const fragment = populateTemplate(template, variables);
        return fragment?.firstElementChild;
    }

    initializeMobileSwiper() {
        if (!this.modal) return;

        const swiperContainer = this.modal.querySelector('[data-mobile-swiper]');
        if (!swiperContainer) return;

        // Destroy existing swiper
        if (this.mobileSwiper) {
            this.mobileSwiper.destroy(true, true);
        }

        // Initialize new Swiper
        this.mobileSwiper = new Swiper(swiperContainer, {
            modules: [Navigation, Pagination],
            direction: 'horizontal',
            loop: false,
            spaceBetween: 16,
            slidesPerView: 1.3,
            slidesOffsetAfter: 64,
            centeredSlides: false,
            speed: 300,
            autoHeight: false,
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                bulletClass: 'w-2 h-2 rounded-full bg-white/40 transition-colors cursor-pointer',
                bulletActiveClass: '!bg-white',
                renderBullet: (_, className) => `<span class="${className}"></span>`
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
            },
            keyboard: {
                enabled: true,
                onlyInViewport: true
            },
            a11y: {
                enabled: true,
                prevSlideMessage: 'Previous bike',
                nextSlideMessage: 'Next bike',
                firstSlideMessage: 'This is the first bike',
                lastSlideMessage: 'This is the last bike'
            },
            breakpoints: {
                640: {
                    slidesPerView: 1.5,
                    spaceBetween: 16
                },
                768: {
                    slidesPerView: 1,
                    spaceBetween: 0
                }
            }
        });
    }

    // Event handlers
    attachEventListeners() {
        // Comparison bar
        this.comparisonBar.addEventListener('click', this.handleComparisonBarClick.bind(this));

        // Document-level click handling
        document.addEventListener('click', this.handleDocumentClick);

        // Keyboard events
        document.addEventListener('keydown', this.handleKeyDown);
    }

    handleComparisonBarClick(e) {
        const target = e.target;

        if (target.closest('[data-clear-all]')) {
            this.handleClearAll();
        } else if (target.closest('[data-compare-now]')) {
            this.showComparisonModal();
        } else if (target.closest('[data-remove]')) {
            const button = target.closest('[data-remove]');
            this.removeBike(button.dataset.bikeId);
        }
    }

    handleClearAll() {
        const message =
            window.themeVariables?.strings?.comparison?.confirm_clear_message ||
            'Are you sure you want to clear all bikes?';

        if (confirm(message)) {
            this.clearAll();
        }
    }

    handleDocumentClick(e) {
        const target = e.target;

        // Modal-related clicks
        if (this.modal && this.modal.contains(target)) {
            if (target.closest('[data-remove-from-comparison]')) {
                const button = target.closest('[data-remove-from-comparison]');
                this.handleRemoveFromTable(button.dataset.bikeId);
            } else if (target.closest('[data-close-modal]') || target.dataset.closeModal === '') {
                this.hideComparisonModal();
            } else if (target.closest('[data-add-to-cart]')) {
                const button = target.closest('[data-add-to-cart]');
                this.handleAddToCart(button.dataset.productHandle);
            }
        }

        // Reload page button
        if (target.closest('[data-reload-page]')) {
            location.reload();
        }
    }

    handleKeyDown(e) {
        if (e.key === 'Escape' && this.modal && !this.modal.classList.contains('hidden')) {
            this.hideComparisonModal();
        }
    }

    handleRemoveFromTable(bikeId) {
        this.removeBike(bikeId);

        if (this.bikes.length < BikeComparison.MIN_BIKES_FOR_COMPARISON) {
            this.hideComparisonModal();
        } else {
            Promise.all([this.updateComparisonTable(), this.updateMobileSlider()]);
        }
    }

    handleAddToCart(productHandle) {
        const currentProductHandle = window.location.pathname.split('/').pop();

        // Track with new analytics system
        trackAddToCartFromComparison({
            product_handle: productHandle,
            source: 'comparison_modal',
            is_current_product: currentProductHandle === productHandle,
            comparison_products_count: this.bikes.length
        });

        // Keep legacy tracking for compatibility
        Shopify.analytics.publish('comparison_product_added_to_cart', {
            product_handle: productHandle
        });

        if (currentProductHandle === productHandle) {
            this.openCustomizationModal();
        } else {
            window.location.href = `/products/${productHandle}?customize=true`;
        }
    }

    openCustomizationModal() {
        const customizationPopup = document.querySelector('customization-popup');
        if (customizationPopup?.open) {
            customizationPopup.open();
        } else {
            const customizeBtn = document.querySelector(
                '[data-open-customization], .customize-btn, .customization-trigger'
            );
            customizeBtn?.click();
        }
    }

    // Utility methods
    async preloadProductData() {
        if (this.bikes.length === 0) return;

        try {
            const missingHandles = this.getMissingHandles();
            if (missingHandles.length > 0) {
                await this.fetchMissingProducts(missingHandles);
            }
            this.isTableLoaded = true;
        } catch (error) {
            console.error('Failed to preload product data:', error);
        }
    }

    showThemeEditorDemo() {
        this.bikes = [
            {
                id: 'demo-1',
                handle: 'demo-bike-1',
                title: 'Demo E-Bike 1',
                image: '//',
                price: '$2,499'
            },
            {
                id: 'demo-2',
                handle: 'demo-bike-2',
                title: 'Demo E-Bike 2',
                image: '//',
                price: '$3,199'
            }
        ];
        this.showComparisonModal();
    }

    // Cleanup
    disconnectedCallback() {
        // Remove event listeners
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleKeyDown);

        // Destroy swiper
        if (this.mobileSwiper) {
            this.mobileSwiper.destroy(true, true);
        }

        // Remove comparison bar
        if (this.comparisonBar) {
            this.comparisonBar.remove();
        }

        // Clear caches
        this.productCache.clear();
        this.uiManager.clearCache();
    }
}

// Register custom element
customElements.define('bike-comparison', BikeComparison);

// Global API
window.bikeComparison = {
    add: function (bikeData) {
        const element = document.querySelector('bike-comparison');
        return element ? element.addBike(bikeData) : false;
    },
    remove: function (bikeId) {
        const element = document.querySelector('bike-comparison');
        return element ? element.removeBike(bikeId) : false;
    },
    clear: function () {
        const element = document.querySelector('bike-comparison');
        if (element) element.clearAll();
    }
};

// Global function for inline onclick handlers in product cards
window.handleCompareClick = function (button) {
    const bikeData = {
        id: button.dataset.bikeId,
        handle: button.dataset.bikeHandle,
        title: button.dataset.bikeTitle,
        image: button.dataset.bikeImage
    };

    const isInComparison = button.classList.contains('bg-green-500');

    if (isInComparison) {
        window.bikeComparison.remove(bikeData.id);
    } else {
        window.bikeComparison.add(bikeData);
    }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBikeComparison);
} else {
    initializeBikeComparison();
}

function initializeBikeComparison() {
    if (!document.querySelector('bike-comparison')) {
        const bikeComparison = document.createElement('bike-comparison');
        document.body.appendChild(bikeComparison);
    }
}

// Export for use in other modules
export { BikeComparison };
